import { graphql } from '@apollo/client/react/hoc';
import { NetworkStatus } from '@apollo/client';
import GET_PRODUCTS_FOR_FINDER_QUERY from './query';
import {
  GetProductsForFinder,
  GetProductsForFinder_getProducts_result_items as FinderProduct,
  GetProductsForFinder_getProducts_result_pageInfo as PageInfo,
} from './__generated__/GetProductsForFinder';
import ProviderResult from '../../../providers/ProviderResult';
import { FinderState } from '../url';

export type ProductsResult = ProviderResult<FinderProduct[], PageInfo>;

export interface FetchMoreOptions {
  variables: { [key: string]: string | null };
}

interface QueryResult extends GetProductsForFinder {
  loading: boolean;
  error: boolean;
  refetch: () => {};
  fetchMore(options?: FetchMoreOptions): FinderProduct[];
  networkStatus: NetworkStatus;
}
interface LoadingState {
  loading: boolean;
}

interface ErrorState {
  error: boolean;
}

interface OutputProps {
  productsResult: ProductsResult;
}

interface Variables {
  condition?: object;
  sortBy?: string;
  search?: string;
  language?: string;
  page?: number;
  hitsPerPage?: number;
}

export interface ProductsProviderInputProps {
  condition?: object;
  searchState?: FinderState;
  language?: string;
  page?: number;
  chunkSize?: number;
}

const PRODUCTS_CHUNK_SIZE = 12;

const ProductsProvider = graphql<
  ProductsProviderInputProps,
  QueryResult,
  Variables,
  LoadingState | ErrorState | OutputProps
>(GET_PRODUCTS_FOR_FINDER_QUERY, {
  options: ({
    condition,
    language,
    searchState,
    page = 0,
    chunkSize = PRODUCTS_CHUNK_SIZE,
  }) => {
    return {
      variables: {
        condition,
        language,
        page,
        hitsPerPage: chunkSize,
        ...(searchState && {
          sortBy: searchState.sorting,
          search: searchState.searching,
        }),
      },
      // enable loading update with fetchMore
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
    };
  },
  props: ({ data }) => {
    const queryResult = data as QueryResult;
    const {
      loading,
      error,
      refetch,
      getProducts,
      fetchMore,
      networkStatus,
    } = queryResult;
    // if variables changed, it indicates that new search was run
    // https://www.apollographql.com/docs/react/api/react-apollo/#datanetworkstatus
    const variablesHaveChanged = networkStatus === 2;
    const productsResult: Omit<ProductsResult, 'pageInfo'> & {
      pageInfo: Omit<ProductsResult['pageInfo'], '__typename'>;
    } = {
      fetchMore,
      refetch: () => refetch(),
      payLoad: [],
      pageInfo: {
        currentPage: null,
        hasNextPage: null,
      },
    };

    // error state
    if (error) {
      return { error, productsResult };
    }

    // loading state
    // https://www.apollographql.com/docs/react/api/react-apollo/#datanetworkstatus
    if (networkStatus === 1) {
      return { loading, productsResult };
    }

    if (!variablesHaveChanged) {
      const { items, pageInfo } = getProducts.result;
      productsResult.payLoad = items;
      productsResult.pageInfo = pageInfo;
    }

    return { productsResult, loading };
  },
});

export default ProductsProvider;
